/**
 * Correo al propietario con su liquidación: textos y adjuntos por defecto.
 * Lo usan el paso 6 del asistente y el envío masivo desde el listado, para
 * que un propietario reciba lo mismo se le mande de una forma o de la otra.
 */

/** «Hola Ana,»; a una empresa no se le saluda por la primera palabra de su razón social */
export function saludo(owner: any): string {
  const completo = `${owner?.firstName || ''} ${owner?.lastName || ''}`.trim();
  if (!completo || /\b(S\.?L\.?U?|S\.?A\.?|S\.?C\.?|C\.?B\.?|SOCIEDAD LIMITADA)\.?$/i.test(completo)) return 'Hola,';
  return `Hola ${String(owner.firstName || '').trim().split(/\s+/)[0]},`;
}

export function textoFacturaPropietario(owner: any, periodo: string, numero: string): string {
  return [
    saludo(owner),
    '',
    `Te enviamos adjunta la factura${numero ? ` ${numero}` : ''} de ${periodo}, que emitimos en tu nombre a Sereno Tech S.L. por la cesión de explotación de tu alojamiento. En ella va también el desglose de las reservas del mes.`,
    '',
    'Si tienes cualquier duda, responde a este correo y lo vemos.',
    '',
    'Un saludo,',
    'El equipo de Sereno',
  ].join('\n');
}

export function textoPredefinido(owner: any, periodo: string, factura?: { draft?: boolean; docNumber?: string | null } | null): string {
  const facturaTxt = !factura
    ? ''
    : factura.draft
      ? ' y el borrador de la factura de nuestros servicios de ese mes'
      : ` y la factura${factura.docNumber ? ` ${factura.docNumber}` : ''} de nuestros servicios de ese mes`;
  return [
    saludo(owner),
    '',
    `Te enviamos adjunta la liquidación de ${periodo} de tu alojamiento${facturaTxt}.`,
    '',
    'Si tienes cualquier duda, responde a este correo y lo vemos.',
    '',
    'Un saludo,',
    'El equipo de Sereno',
  ].join('\n');
}

/** Número de la factura de propietario (explotación), si tiene. */
export function numeroFacturaPropietario(liq: any): string {
  return liq?.result?.tipo === 'factura' && liq.invoiceNum != null ? `${liq.invoicePrefix ?? ''}${liq.invoiceNum}` : '';
}

export interface CorreoPorDefecto {
  asunto: string;
  texto: string;
  /** Adjuntar la factura de Holded (solo gestión, y si existe) */
  adjuntarFactura: boolean;
  /** Qué adjuntos lleva, para enseñarlo antes de enviar */
  adjuntos: { texto: string; aviso?: string }[];
}

/**
 * Lo que se le manda a un propietario por defecto. En explotación la
 * liquidación es la factura de propietario y va sola; en gestión va la
 * liquidación y, si existe, la factura de Holded (aprobada o en borrador).
 */
export function correoPorDefecto(liq: any, owner: any): CorreoPorDefecto {
  if (liq?.result?.tipo === 'factura') {
    const numero = numeroFacturaPropietario(liq);
    return {
      asunto: `Factura de ${liq.periodo} · Sereno`,
      texto: textoFacturaPropietario(owner, liq.periodo, numero),
      adjuntarFactura: false,
      adjuntos: [{ texto: `Factura de propietario${numero ? ` ${numero}` : ''}`, aviso: numero ? undefined : 'sin número' }],
    };
  }
  const factura = liq?.holdedInvoice || null;
  return {
    asunto: `Liquidación de ${liq.periodo} · Sereno`,
    texto: textoPredefinido(owner, liq.periodo, factura),
    adjuntarFactura: Boolean(factura),
    adjuntos: [
      { texto: `Liquidación de ${liq.periodo}` },
      ...(factura
        ? [factura.draft
          ? { texto: 'Borrador de la factura de Holded', aviso: 'borrador, sin número' }
          : { texto: `Factura ${factura.docNumber || ''} de Holded`.replace(/\s+/g, ' ') }]
        : []),
    ],
  };
}

// ─── Seguimiento: entregado, abierto, rebotado ─────────────────────────────

export type TipoEstadoCorreo = 'enviado' | 'entregado' | 'abierto' | 'no_entregado';

const fechaCorta = (iso: string) =>
  new Date(iso).toLocaleString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

const MOTIVO_NO_ENTREGADO: Record<string, string> = {
  Bounced: 'la dirección no existe o rechaza el correo',
  Suppressed: 'esa dirección ya rebotó antes y Azure no le envía',
  Quarantined: 'retenido como spam en el servidor del destinatario',
  FilteredSpam: 'rechazado como spam',
  Failed: 'no se pudo entregar',
};

/**
 * Lo último que se sabe de un correo enviado, a partir de los avisos de Azure.
 * «Abierto» no es infalible: el correo del iPhone descarga las imágenes por su
 * cuenta y puede marcarlo sin que se haya leído, y quien bloquea imágenes
 * nunca sale como abierto.
 */
export function estadoCorreo(envio: any): { tipo: TipoEstadoCorreo; texto: string } {
  if (envio?.entrega && !envio.entrega.ok) {
    return { tipo: 'no_entregado', texto: `No entregado: ${MOTIVO_NO_ENTREGADO[envio.entrega.estado] || envio.entrega.estado}` };
  }
  if (envio?.abierto) {
    const veces = envio.abierto.veces > 1 ? ` (${envio.abierto.veces} veces)` : '';
    return { tipo: 'abierto', texto: `Abierto el ${fechaCorta(envio.abierto.primera)}${veces}` };
  }
  if (envio?.entrega?.ok) return { tipo: 'entregado', texto: 'Entregado, sin abrir todavía' };
  return { tipo: 'enviado', texto: envio?.messageId ? 'Enviado, esperando aviso de entrega' : 'Enviado' };
}

/** El último envío correcto de una liquidación, si lo hay */
export function ultimoEnvio(liq: any): any | null {
  return [...(liq?.emailsEnviados || [])].reverse().find((e: any) => e.estado === 'enviado') || null;
}

// ─── Programado para más tarde ──────────────────────────────────────────────

/** El correo programado de una liquidación (pendiente, saliendo o fallido), si lo hay */
export function correoProgramado(liq: any): any | null {
  return liq?.correoProgramado || null;
}

/** «el miércoles 22 de octubre a las 09:00» */
export function fechaProgramada(iso: string): string {
  const d = new Date(iso);
  // «jueves, 22 de octubre» sin la coma, que en mitad de una frase sobra
  const dia = d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' }).replace(',', '');
  const hora = d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  return `el ${dia} a las ${hora}`;
}

export function textoProgramado(p: any): string {
  if (p.estado === 'fallido') return `El envío programado para ${fechaProgramada(p.programadoPara)} no salió: ${p.detalle || 'error'}`;
  if (p.estado === 'enviando') return `Saliendo ahora (programado para ${fechaProgramada(p.programadoPara)})`;
  return `Programado para ${fechaProgramada(p.programadoPara)}`;
}

/** Valor para un <input type="datetime-local">, en la hora del navegador */
export function aDatetimeLocal(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** Lo que se propone al programar: el siguiente día laborable a las 9:00 */
export function horaPorDefecto(ahora = new Date()): Date {
  const d = new Date(ahora);
  d.setDate(d.getDate() + 1);
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  return d;
}
